import { useState, useRef, useEffect } from 'react';
import { Monitor, Smartphone } from 'lucide-react';
import { cn } from '../../lib/cn';

interface EmailPreviewProps {
  html: string;
}

export function EmailPreview({ html }: EmailPreviewProps) {
  const [viewMode, setViewMode] = useState<'desktop' | 'mobile'>('desktop');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (iframeRef.current) {
      const doc = iframeRef.current.contentDocument;
      if (doc) {
        doc.open();
        doc.write(html);
        doc.close();
      }
    }
  }, [html]);

  const width = viewMode === 'desktop' ? 600 : 320;

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50">
        <span className="text-sm font-medium text-gray-700">Preview</span>
        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-0.5">
          <button
            type="button"
            onClick={() => setViewMode('desktop')}
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
              viewMode === 'desktop'
                ? 'bg-primary-100 text-primary-700'
                : 'text-gray-500 hover:text-gray-700'
            )}
          >
            <Monitor className="h-3.5 w-3.5" />
            Desktop
          </button>
          <button
            type="button"
            onClick={() => setViewMode('mobile')}
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
              viewMode === 'mobile'
                ? 'bg-primary-100 text-primary-700'
                : 'text-gray-500 hover:text-gray-700'
            )}
          >
            <Smartphone className="h-3.5 w-3.5" />
            Mobile
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-auto bg-gray-100 p-4 flex justify-center">
        <div
          style={{ width: `${width}px`, maxWidth: '100%' }}
          className="bg-white shadow-lg rounded-lg overflow-hidden transition-all duration-300"
        >
          <iframe
            ref={iframeRef}
            title="Email Preview"
            className="w-full border-0"
            style={{ height: '800px' }}
            sandbox="allow-same-origin"
          />
        </div>
      </div>
    </div>
  );
}
